import { createUseStyles } from "react-jss";
import type { Theme } from "../../types/Theme";
import {
    MODERN_TIMER_LABEL_FONT_WEIGHT,
    MODERN_TIMER_PULSE_DURATION_MS,
    MODERN_TIMER_READOUT_BREAKPOINT_PX,
    MODERN_TIMER_READOUT_FONT_WEIGHT,
    MODERN_TIMER_READOUT_LETTER_SPACING,
    MODERN_TIMER_READOUT_MONO_FONT,
    MODERN_TIMER_READOUT_SIZE_DESKTOP_PX,
    MODERN_TIMER_READOUT_SIZE_MOBILE_PX,
    resolveModernTimerForegroundColor,
    resolveModernTimerLabelFontSize,
    resolveModernTimerReadoutColor,
    useModernTimerStyles,
} from "../../theme/timerThemeTokens";

interface TimerStyleProps {
    theme: Theme;
    isEnding?: boolean;
}

export default createUseStyles({
    "@keyframes timerReadoutPulse": {
        "0%, 100%": { opacity: 1 },
        "50%": { opacity: 0.5 },
    },
    timerLabel: {
        composes: "timerLabel",
        fontFamily: ({ theme }: TimerStyleProps) =>
            useModernTimerStyles(theme) ? `"${theme.TextFont}", sans-serif` : undefined,
        fontSize: ({ theme }: TimerStyleProps) =>
            useModernTimerStyles(theme) ? resolveModernTimerLabelFontSize(theme) : undefined,
        fontWeight: ({ theme }: TimerStyleProps) =>
            useModernTimerStyles(theme) ? MODERN_TIMER_LABEL_FONT_WEIGHT : undefined,
        color: ({ theme }: TimerStyleProps) =>
            useModernTimerStyles(theme) ? resolveModernTimerForegroundColor() : undefined,
    },
    timerTime: {
        composes: "timerTime",
        fontFamily: ({ theme }: TimerStyleProps) =>
            useModernTimerStyles(theme) ? MODERN_TIMER_READOUT_MONO_FONT : undefined,
        fontSize: ({ theme }: TimerStyleProps) =>
            useModernTimerStyles(theme)
                ? `${MODERN_TIMER_READOUT_SIZE_MOBILE_PX}px`
                : undefined,
        fontWeight: ({ theme }: TimerStyleProps) =>
            useModernTimerStyles(theme) ? MODERN_TIMER_READOUT_FONT_WEIGHT : undefined,
        fontVariantNumeric: ({ theme }: TimerStyleProps) =>
            useModernTimerStyles(theme) ? "tabular-nums" : undefined,
        lineHeight: ({ theme }: TimerStyleProps) =>
            useModernTimerStyles(theme) ? 1 : undefined,
        letterSpacing: ({ theme }: TimerStyleProps) =>
            useModernTimerStyles(theme) ? MODERN_TIMER_READOUT_LETTER_SPACING : undefined,
        color: ({ theme, isEnding }: TimerStyleProps) =>
            useModernTimerStyles(theme)
                ? resolveModernTimerReadoutColor(Boolean(isEnding))
                : undefined,
        transition: ({ theme }: TimerStyleProps) =>
            useModernTimerStyles(theme) ? "color 0.2s ease" : undefined,
        animation: ({ theme, isEnding }: TimerStyleProps) =>
            useModernTimerStyles(theme) && isEnding
                ? `$timerReadoutPulse ${MODERN_TIMER_PULSE_DURATION_MS}ms cubic-bezier(0.4, 0, 0.6, 1) infinite`
                : undefined,
        "@media (prefers-reduced-motion: reduce)": {
            animation: ({ theme, isEnding }: TimerStyleProps) =>
                useModernTimerStyles(theme) && isEnding ? "none" : undefined,
        },
        [`@media (min-width: ${MODERN_TIMER_READOUT_BREAKPOINT_PX}px)`]: {
            fontSize: ({ theme }: TimerStyleProps) =>
                useModernTimerStyles(theme)
                    ? `${MODERN_TIMER_READOUT_SIZE_DESKTOP_PX}px`
                    : undefined,
        },
    },
});
